import React from 'react';
import { Container, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * SuitabilityReport - Full page printable report
 * Based on Mark's template
 */

const SuitabilityReport = ({
                               personalProfile,
                               financialProfile,
                               goals,
                               contributionDetails,
                               riskAssessment,
                               fundRecommendations,
                               contributionRecommendation,
                               backendRecommendation,
                               onBack  // Callback to return to recommendations
                           }) => {

    const formatCurrency = (value) => new Intl.NumberFormat('en-GB', {
        style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(value || 0);

    const formatDate = () => {
        return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const getRiskLabel = (score) => {
        if (score === null || score === undefined) return 'N/A';
        if (score <= 0.15) return 'Very Low';
        if (score <= 0.30) return 'Low';
        if (score <= 0.44) return 'Low-Medium';
        if (score <= 0.58) return 'Medium';
        if (score <= 0.72) return 'Medium-High';
        if (score <= 0.86) return 'High';
        return 'Very High';
    };

    const handlePrint = () => {
        window.print();
    };

    // Get contribution advice - try multiple sources
    const contributionAdvice = backendRecommendation?.contribution_advice ||
        contributionRecommendation ||
        null;

    const monthlyContribution = contributionAdvice?.monthly_amount ||
        contributionAdvice?.recommended_monthly_contribution ||
        0;

    // Calculate totals
    const currentAnnualEmployee = (contributionDetails?.employeeContribution || 0) / 100 * (financialProfile?.annualSalary || 0);
    const currentAnnualEmployer = (contributionDetails?.employerContribution || 0) / 100 * (financialProfile?.annualSalary || 0);
    const totalTER = fundRecommendations?.reduce((sum, f) => sum + (f.fund?.ter || 0) * (f.allocation_percentage || 0) / 100, 0) || 0;

    return (
        <div className="suitability-report-page">
            {/* Fixed header with buttons - hidden when printing */}
            <div className="no-print report-toolbar">
                <Container>
                    <div className="d-flex justify-content-between align-items-center py-3">
                        <Button variant="outline-secondary" onClick={onBack}>
                            <FontAwesomeIcon icon="arrow-left" className="me-2" />
                            Back to Recommendations
                        </Button>
                        <Button variant="primary" onClick={handlePrint}>
                            <FontAwesomeIcon icon="download" className="me-2" />
                            Print / Save as PDF
                        </Button>
                    </div>
                </Container>
            </div>

            {/* Report content */}
            <Container className="report-content py-4">
                <header className="report-header text-center mb-5">
                    <h1>Financial Advice Suitability Report</h1>
                    <p className="text-muted">Generated by Waive</p>
                </header>

                <div className="report-meta mb-4">
                    <p><strong>Client:</strong> {personalProfile?.name || 'Client'}</p>
                    <p><strong>Date:</strong> {formatDate()}</p>
                </div>

                {/* Section 1: Purpose */}
                <section className="report-section">
                    <h2>1. Purpose of This Report</h2>
                    <p>
                        This report outlines our recommendations regarding pension contribution levels,
                        investment fund selection, and ongoing review strategy based on the information
                        you have provided through the Waive assessment process.
                    </p>
                </section>

                {/* Section 2: Current Circumstances */}
                <section className="report-section">
                    <h2>2. Current Circumstances</h2>
                    <table className="report-table">
                        <tbody>
                        <tr><td>Age</td><td>{personalProfile?.age || 'N/A'}</td></tr>
                        <tr><td>Planned retirement age</td><td>{goals?.targetRetirementAge || 'N/A'}</td></tr>
                        <tr><td>Years to retirement</td><td>{goals?.yearsToRetirement || 'N/A'}</td></tr>
                        <tr><td>Gross salary</td><td>{formatCurrency(financialProfile?.annualSalary)} per annum</td></tr>
                        <tr><td>Current pension value</td><td>{formatCurrency(financialProfile?.pensionValue)}</td></tr>
                        <tr><td>Current contributions</td><td>{contributionDetails?.employeeContribution || 0}% employee / {contributionDetails?.employerContribution || 0}% employer</td></tr>
                        <tr><td>Target retirement income</td><td>{formatCurrency(goals?.incomeInRetirement)} per year</td></tr>
                        </tbody>
                    </table>
                </section>

                {/* Section 3: Risk */}
                <section className="report-section">
                    <h2>3. Attitude to Risk & Capacity for Loss</h2>
                    <p>
                        <strong>Risk Tolerance:</strong> {getRiskLabel(riskAssessment?.risk_tolerance_score)}
                    </p>
                    <p>
                        <strong>Capacity for Loss:</strong> {getRiskLabel(riskAssessment?.risk_capacity_score)}
                    </p>
                    <p>
                        <strong>Overall Risk Profile:</strong> SRRI {riskAssessment?.max_srri || 'N/A'}/7
                        {riskAssessment?.limiting_factor && (
                            <span className="text-muted"> (limited by {riskAssessment.limiting_factor})</span>
                        )}
                    </p>
                </section>

                {/* Section 4: Contribution Recommendation */}
                <section className="report-section">
                    <h2>4. Contribution Recommendation</h2>
                    {monthlyContribution > 0 ? (
                        <>
                            <p>
                                <strong>Recommendation:</strong> Contribute {formatCurrency(monthlyContribution)} per month to your pension.
                            </p>
                            <table className="report-table">
                                <tbody>
                                <tr>
                                    <td>Current annual contributions</td>
                                    <td>{formatCurrency(currentAnnualEmployee + currentAnnualEmployer)} ({formatCurrency(currentAnnualEmployee)} employee + {formatCurrency(currentAnnualEmployer)} employer)</td>
                                </tr>
                                <tr>
                                    <td>Recommended monthly contribution</td>
                                    <td>{formatCurrency(monthlyContribution)}</td>
                                </tr>
                                <tr>
                                    <td>Recommended annual contribution</td>
                                    <td>{formatCurrency(monthlyContribution * 12)}</td>
                                </tr>
                                {contributionAdvice?.tax_relief_amount > 0 && (
                                    <tr>
                                        <td>Estimated annual tax relief</td>
                                        <td>{formatCurrency(contributionAdvice.tax_relief_amount)}</td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                            {contributionAdvice?.contribution_rationale && (
                                <p className="mt-2 text-muted">{contributionAdvice.contribution_rationale}</p>
                            )}
                        </>
                    ) : (
                        <p>Maintain your current contribution levels.</p>
                    )}
                </section>

                {/* Section 5: Investment Recommendation */}
                <section className="report-section">
                    <h2>5. Investment Recommendation</h2>
                    {fundRecommendations && fundRecommendations.length > 0 ? (
                        <>
                            <p><strong>Recommended allocation:</strong></p>
                            <table className="report-table">
                                <thead>
                                <tr>
                                    <th>Fund</th>
                                    <th>Allocation</th>
                                    <th>SRRI</th>
                                    <th>TER</th>
                                </tr>
                                </thead>
                                <tbody>
                                {fundRecommendations.map((rec, idx) => (
                                    <tr key={idx}>
                                        <td>{rec.fund?.name || 'Unknown Fund'}</td>
                                        <td>{rec.allocation_percentage}%</td>
                                        <td>{rec.fund?.srri || 'N/A'}</td>
                                        <td>{(rec.fund?.ter || 0).toFixed(2)}%</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            <p className="mt-2">
                                This allocation aligns with your risk profile and provides diversification
                                with a total estimated ongoing charge of {totalTER.toFixed(2)}%.
                            </p>
                        </>
                    ) : (
                        <p>No fund recommendations available.</p>
                    )}
                </section>

                {/* Section 6: Charges */}
                <section className="report-section">
                    <h2>6. Charges and Costs</h2>
                    <ul>
                        <li>Total estimated fund charges: {totalTER.toFixed(2)}% p.a.</li>
                        <li>Platform charges may apply (check with your provider)</li>
                    </ul>
                </section>

                {/* Section 7: Risks */}
                <section className="report-section">
                    <h2>7. Key Risks</h2>
                    <ul>
                        <li>Investment values can fall as well as rise.</li>
                        <li>Equity markets are volatile in the short term.</li>
                        <li>Pension legislation and tax rules may change.</li>
                        <li>Higher contributions reduce current disposable income.</li>
                        <li>Past performance is not a guide to future performance.</li>
                    </ul>
                </section>

                {/* Section 8: Tax */}
                <section className="report-section">
                    <h2>8. Tax Considerations</h2>
                    <ul>
                        <li>Contributions receive tax relief at your marginal rate</li>
                        <li>Annual allowance currently £60,000 (subject to tapering for high earners)</li>
                        <li>Funds grow free of income and capital gains tax</li>
                        <li>25% of pension may normally be taken tax-free at retirement (subject to limits)</li>
                    </ul>
                </section>

                {/* Section 9: Review */}
                <section className="report-section">
                    <h2>9. Ongoing Review</h2>
                    <p>
                        Annual review of contribution levels and investment allocation recommended.
                        Gradual de-risking should be considered 5-10 years prior to retirement.
                    </p>
                </section>

                {/* Footer */}
                <footer className="report-footer mt-5 pt-3 border-top">
                    <p className="text-muted small">
                        This report has been generated by Waive using FCA-compliant methodology.
                        The recommendations are based on the information provided and are for guidance only.
                        Please consult a qualified financial adviser before making any financial decisions.
                    </p>
                    <p className="text-muted small">
                        Report generated: {formatDate()}
                    </p>
                </footer>

                {/* Back button at bottom - hidden when printing */}
                <div className="no-print mt-4 text-center">
                    <Button variant="outline-secondary" onClick={onBack}>
                        <FontAwesomeIcon icon="arrow-left" className="me-2" />
                        Back to Recommendations
                    </Button>
                </div>
            </Container>

            {/* Styles */}
            <style>{`
        .suitability-report-page {
          background: #fff;
          min-height: 100vh;
        }
        .report-toolbar {
          background: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .report-content {
          max-width: 800px;
        }
        .report-section { margin-bottom: 2rem; }
        .report-section h2 { 
          font-size: 1.25rem; 
          border-bottom: 2px solid #0d6efd; 
          padding-bottom: 0.5rem; 
          margin-bottom: 1rem; 
        }
        .report-table { width: 100%; margin: 1rem 0; }
        .report-table td, .report-table th { 
          padding: 0.5rem; 
          border: 1px solid #dee2e6; 
        }
        .report-table th { background: #f8f9fa; }
        
        @media print {
          .no-print { display: none !important; }
          .report-toolbar { display: none !important; }
          .report-content { 
            font-size: 12pt; 
            max-width: 100%;
            padding: 0;
          }
          .report-section { page-break-inside: avoid; margin-bottom: 20px; }
          .report-section h2 { 
            font-size: 14pt; 
            border-bottom: 1px solid #333; 
            padding-bottom: 5px; 
          }
          .report-table { border-collapse: collapse; }
          .report-table td, .report-table th { 
            border: 1px solid #333; 
            padding: 8px; 
          }
          .report-table th { background: #eee; }
          .report-header h1 { font-size: 18pt; }
        }
      `}</style>
        </div>
    );
};

export default SuitabilityReport;